/**
 * Insert a newline at the caret for controlled React textareas (dispatches `input` so `onChange` runs).
 */
function insertNewlineAtCursor(textarea) {
  if (!textarea || textarea.tagName !== "TEXTAREA") return;
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const prev = textarea.value;
  const next = prev.slice(0, start) + "\n" + prev.slice(end);
  const desc = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value");
  const nativeSetter = desc?.set;
  if (nativeSetter) {
    nativeSetter.call(textarea, next);
  } else {
    textarea.value = next;
  }
  textarea.dispatchEvent(new Event("input", { bubbles: true }));
  const cursor = start + 1;
  requestAnimationFrame(() => {
    textarea.setSelectionRange(cursor, cursor);
  });
}

/**
 * For inline / edit-on-click fields that save on blur: **Enter** blurs (same as single-line fields).
 *
 * - Skips during IME composition (`isComposing`).
 * - For **textarea**, **Ctrl+Enter** (Windows/Linux) or **Cmd+Enter** (macOS) inserts a newline.
 */
export function fieldCommitOnEnterKeyDown(e) {
  if (e.key !== "Enter") return;
  if (e.nativeEvent?.isComposing) return;
  const el = e.currentTarget;

  if (el && el.tagName === "TEXTAREA") {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      insertNewlineAtCursor(el);
      return;
    }
    e.preventDefault();
    if (typeof el.blur === "function") el.blur();
    return;
  }

  e.preventDefault();
  if (typeof el?.blur === "function") el.blur();
}

/**
 * `<input type="date">`: some browsers omit keydown Enter for blur-save flows. Pair with
 * `onKeyDown={fieldCommitOnEnterKeyDown}` on the same input.
 */
export function fieldCommitDateInputKeyUp(e) {
  if (e.key !== "Enter" || e.nativeEvent?.isComposing) return;
  if (document.activeElement === e.currentTarget && typeof e.currentTarget.blur === "function") {
    e.currentTarget.blur();
  }
}

/**
 * Inside `<form>`, Enter submits when native behavior does not (e.g. `type="date"`, custom `TimeSelect` trigger).
 */
export function submitClosestFormOnEnterKeyDown(e) {
  if (e.key !== "Enter" || e.nativeEvent?.isComposing) return;
  const root = e.currentTarget;
  const form = root && typeof root.closest === "function" ? root.closest("form") : null;
  if (!form || typeof form.requestSubmit !== "function") return;
  e.preventDefault();
  form.requestSubmit();
}

/**
 * `<input type="date">` inside a form: keyup Enter fallback when keydown did not submit.
 */
export function submitClosestFormOnDateInputKeyUp(e) {
  if (e.key !== "Enter" || e.nativeEvent?.isComposing) return;
  if (document.activeElement !== e.currentTarget) return;
  const form = e.currentTarget.form;
  if (form && typeof form.requestSubmit === "function") {
    e.preventDefault();
    form.requestSubmit();
  }
}
